import React, { useState, useRef, useCallback, useMemo } from 'react';
import { WorkflowNode } from './WorkflowNode';
import Modal from '../ui/Modal';
import { WorkflowCanvasState, WorkflowNode as WorkflowNodeType, WorkflowConnection, NodeTemplate } from '../../types/workflow';
import { TransitionConditionType } from '../../types/workflow-spec';
import { useThemeColor } from '../../context/BrandingContext';
import { cssColor } from '../../theme/engine';
import { Button } from '../ui/Button';
import { SelectField, TextField } from '../ui/Field';

interface WorkflowCanvasProps {
  canvasState: WorkflowCanvasState;
  onUpdateCanvasState: (updates: Partial<WorkflowCanvasState>) => void;
  onUpdateNode: (nodeId: string, updates: Partial<WorkflowNodeType>) => void;
  onDeleteNode: (nodeId: string) => void;
  onAddConnection: (connection: WorkflowConnection) => void;
  onDeleteConnection: (connectionId: string) => void;
  onAddNode: (template: NodeTemplate, position?: { x: number; y: number }) => void;
  onAddRouterBranch?: (nodeId: string) => void;
  onOpenAgentConfig?: (agentId: string) => void;
}

export const WorkflowCanvas: React.FC<WorkflowCanvasProps> = ({
  canvasState,
  onUpdateCanvasState,
  onUpdateNode,
  onDeleteNode,
  onAddConnection,
  onDeleteConnection,
  onAddNode,
  onAddRouterBranch,
  onOpenAgentConfig,
}) => {
  const themeColor = useThemeColor();
  const canvasRef = useRef<HTMLDivElement>(null);
  const portAnchorsRef = useRef<Record<string, { x: number; y: number }>>({});
  const NODE_WIDTH = 192; // must mirror node width in UI
  const NODE_BASE_HEIGHT = 112;
  const PORT_ROW_HEIGHT = 24;
  const DEFAULT_NODE_HEIGHT = 220;
  const VIEWPORT_PADDING = 96;
  const CONDITION_TYPE_OPTIONS: TransitionConditionType[] = ['custom', 'intent', 'outcome', 'user-response', 'fallback'];
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [canvasOffset, setCanvasOffset] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [isDragOver, setIsDragOver] = useState(false);
  const [draggingNodeId, setDraggingNodeId] = useState<string | null>(null);
  const [nodeDragDelta, setNodeDragDelta] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [connectionPreview, setConnectionPreview] = useState<{ x: number; y: number } | null>(null);
  const [hoverInput, setHoverInput] = useState<{ nodeId: string; inputId: string; rect: DOMRect } | null>(null);
  const [pendingConnection, setPendingConnection] = useState<{
    sourceNodeId: string;
    sourceOutputId: string;
    targetNodeId: string;
    targetInputId: string;
  } | null>(null);
  const [pendingConnectionForm, setPendingConnectionForm] = useState<{
    label: string;
    conditionType: TransitionConditionType;
  }>({
    label: 'Next',
    conditionType: 'custom',
  });
  const getNodeHeight = useCallback((node: WorkflowNodeType) => {
    const branchCount = node.outputs?.length ?? 0;
    const inputCount = node.inputs?.length ?? 0;
    const summaryHeight = node.summary ? 56 : 0;
    const routerHeight = node.nodeClass === 'router' ? 38 : 0;
    return NODE_BASE_HEIGHT + summaryHeight + Math.max(inputCount, branchCount) * PORT_ROW_HEIGHT + routerHeight;
  }, []);

  const getFlowBounds = useCallback(() => {
    if (canvasState.nodes.length === 0) {
      return {
        minX: 0,
        minY: 0,
        maxX: 0,
        maxY: 0,
        width: 0,
        height: 0,
      };
    }

    const minX = Math.min(...canvasState.nodes.map((node) => node.position.x));
    const minY = Math.min(...canvasState.nodes.map((node) => node.position.y));
    const maxX = Math.max(...canvasState.nodes.map((node) => node.position.x + NODE_WIDTH));
    const maxY = Math.max(...canvasState.nodes.map((node) => node.position.y + getNodeHeight(node)));

    return {
      minX,
      minY,
      maxX,
      maxY,
      width: maxX - minX,
      height: maxY - minY,
    };
  }, [canvasState.nodes, getNodeHeight]);

  const getOutputAnchor = useCallback((node: WorkflowNodeType, outputId: string) => {
    const preciseAnchor = portAnchorsRef.current[`${node.id}:output:${outputId}`];
    if (preciseAnchor) {
      return {
        x: node.position.x + preciseAnchor.x,
        y: node.position.y + preciseAnchor.y,
      };
    }
    const outputIndex = Math.max(0, node.outputs.findIndex((output) => output.id === outputId));
    const rowOffset = (node.inputs?.length ?? 0) * PORT_ROW_HEIGHT;
    return {
      x: node.position.x + NODE_WIDTH,
      y: node.position.y + NODE_BASE_HEIGHT + rowOffset + 18 + outputIndex * PORT_ROW_HEIGHT,
    };
  }, []);

  const getInputAnchor = useCallback((node: WorkflowNodeType, inputId: string) => {
    const preciseAnchor = portAnchorsRef.current[`${node.id}:input:${inputId}`];
    if (preciseAnchor) {
      return {
        x: node.position.x + preciseAnchor.x,
        y: node.position.y + preciseAnchor.y,
      };
    }
    const inputIndex = Math.max(0, node.inputs.findIndex((input) => input.id === inputId));
    return {
      x: node.position.x,
      y: node.position.y + NODE_BASE_HEIGHT + 18 + inputIndex * PORT_ROW_HEIGHT,
    };
  }, []);

  const getBezierControls = useCallback((sourceX: number, sourceY: number, targetX: number, targetY: number) => {
    const deltaX = targetX - sourceX;
    const curvature = Math.max(44, Math.min(140, Math.abs(deltaX) * 0.28));
    return {
      c1x: sourceX + curvature,
      c1y: sourceY,
      c2x: targetX - curvature,
      c2y: targetY,
    };
  }, []);

  const getBezierPoint = useCallback((
    sourceX: number,
    sourceY: number,
    targetX: number,
    targetY: number,
    t: number
  ) => {
    const controls = getBezierControls(sourceX, sourceY, targetX, targetY);
    const mt = 1 - t;
    const x =
      mt ** 3 * sourceX +
      3 * mt ** 2 * t * controls.c1x +
      3 * mt * t ** 2 * controls.c2x +
      t ** 3 * targetX;
    const y =
      mt ** 3 * sourceY +
      3 * mt ** 2 * t * controls.c1y +
      3 * mt * t ** 2 * controls.c2y +
      t ** 3 * targetY;
    return { x, y, ...controls };
  }, [getBezierControls]);

  const getBezierNormal = useCallback((
    sourceX: number,
    sourceY: number,
    targetX: number,
    targetY: number,
    t: number
  ) => {
    const controls = getBezierControls(sourceX, sourceY, targetX, targetY);
    const mt = 1 - t;
    const dx =
      3 * mt ** 2 * (controls.c1x - sourceX) +
      6 * mt * t * (controls.c2x - controls.c1x) +
      3 * t ** 2 * (targetX - controls.c2x);
    const dy =
      3 * mt ** 2 * (controls.c1y - sourceY) +
      6 * mt * t * (controls.c2y - controls.c1y) +
      3 * t ** 2 * (targetY - controls.c2y);
    const magnitude = Math.max(1, Math.sqrt(dx * dx + dy * dy));
    return { x: -dy / magnitude, y: dx / magnitude };
  }, [getBezierControls]);

  const canvasBounds = useMemo(() => getFlowBounds(), [getFlowBounds]);

  const handleCanvasClick = useCallback((e: React.MouseEvent) => {
    if (e.target === canvasRef.current) {
      onUpdateCanvasState({
        selectedNodeId: undefined,
        selectedConnectionId: undefined,
        isConnecting: false,
        connectionStart: undefined,
      });
    }
  }, [onUpdateCanvasState]);

  const handleCanvasMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.target === canvasRef.current) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - canvasOffset.x, y: e.clientY - canvasOffset.y });
    }
  }, [canvasOffset]);

  const handleCanvasMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging) {
      setCanvasOffset({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
    // Node dragging
    if (draggingNodeId) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const x = (e.clientX - rect.left) / zoom - nodeDragDelta.x;
      const y = (e.clientY - rect.top) / zoom - nodeDragDelta.y;
      onUpdateNode(draggingNodeId, {
        position: {
          x: Math.max(0, x),
          y: Math.max(0, y),
        },
      });
    }
    if (canvasState.isConnecting) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const x = (e.clientX - rect.left) / zoom;
      const y = (e.clientY - rect.top) / zoom;
      setConnectionPreview({ x, y });
    }
  }, [isDragging, dragStart, draggingNodeId, nodeDragDelta.x, nodeDragDelta.y, zoom, onUpdateNode, canvasState.isConnecting]);

  const handleCanvasMouseUp = useCallback(() => {
    setIsDragging(false);
    setDraggingNodeId(null);
  }, []);

  const centerToFlow = useCallback(() => {
    const rect = canvasRef.current?.parentElement?.getBoundingClientRect();
    if (!rect || canvasState.nodes.length === 0) return;
    const bounds = getFlowBounds();
    const targetCenterX = rect.width / 2;
    const targetCenterY = rect.height / 2;
    const flowCenterX = (bounds.minX + bounds.maxX) / 2;
    const flowCenterY = (bounds.minY + bounds.maxY) / 2;

    setCanvasOffset({
      x: targetCenterX - flowCenterX * zoom,
      y: targetCenterY - flowCenterY * zoom,
    });
  }, [canvasState.nodes.length, getFlowBounds, zoom]);

  const fitToFlow = useCallback(() => {
    const rect = canvasRef.current?.parentElement?.getBoundingClientRect();
    if (!rect || canvasState.nodes.length === 0) return;
    const bounds = getFlowBounds();
    const widthScale = (rect.width - VIEWPORT_PADDING * 2) / Math.max(bounds.width, 1);
    const heightScale = (rect.height - VIEWPORT_PADDING * 2) / Math.max(bounds.height, 1);
    const nextZoom = Math.max(0.45, Math.min(1.2, Math.min(widthScale, heightScale)));
    setZoom(nextZoom);
    setCanvasOffset({
      x: rect.width / 2 - ((bounds.minX + bounds.maxX) / 2) * nextZoom,
      y: rect.height / 2 - ((bounds.minY + bounds.maxY) / 2) * nextZoom,
    });
  }, [canvasState.nodes.length, getFlowBounds]);

  const handleCanvasDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    console.log('Canvas drop event triggered');
    
    try {
      // Try preferred MIME type first, then fallback to text/plain for Safari
      const templateData =
        e.dataTransfer.getData('application/json') || e.dataTransfer.getData('text/plain');
      console.log('Retrieved template data:', templateData);
      
      let template: NodeTemplate | undefined;
      if (templateData) {
        template = JSON.parse(templateData);
        console.log('Parsed template:', template);
      }

      // Fallback template if parsing failed or data missing
      if (!template) {
        template = {
          type: 'ai-agent' as any,
          name: 'Node',
          description: 'Dropped node',
          icon: '🔧',
          category: 'utilities',
          color: '#3b82f6',
          inputs: [],
          outputs: [],
          defaultData: {},
        };
      }

      if (template) {
        const rect = canvasRef.current?.getBoundingClientRect();
        console.log('Canvas rect:', rect);
        
        if (rect) {
          // rect already includes the applied CSS transform (translate/scale),
          // so subtracting canvasOffset again would double-count it.
          let x = (e.clientX - rect.left) / zoom;
          let y = (e.clientY - rect.top) / zoom;

          // Clamp to keep node fully within canvas visible bounds
          const unscaledWidth = rect.width / zoom;
          const unscaledHeight = rect.height / zoom;
          x = Math.max(0, Math.min(unscaledWidth - NODE_WIDTH, x));
          y = Math.max(0, Math.min(unscaledHeight - DEFAULT_NODE_HEIGHT, y));

          console.log('Calculated clamped position:', { x, y });
          onAddNode(template, { x, y });
        }
      }
    } catch (error) {
      console.error('Error handling drop:', error);
    }
  }, [DEFAULT_NODE_HEIGHT, zoom, onAddNode]);

  const handleCanvasDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
    console.log('Drag over canvas');
  }, []);

  const handleCanvasDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom(prev => Math.max(0.1, Math.min(3, prev * delta)));
  }, []);

  const handleNodeSelect = useCallback((nodeId: string) => {
    onUpdateCanvasState({ selectedNodeId: nodeId, selectedConnectionId: undefined });
  }, [onUpdateCanvasState]);

  const handleBeginDragNode = useCallback((nodeId: string, e: React.MouseEvent) => {
    // Only start dragging when not interacting with connection dots
    setDraggingNodeId(nodeId);
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const node = canvasState.nodes.find(n => n.id === nodeId);
    if (!node) return;
    const cursorX = (e.clientX - rect.left) / zoom;
    const cursorY = (e.clientY - rect.top) / zoom;
    setNodeDragDelta({ x: cursorX - node.position.x, y: cursorY - node.position.y });
  }, [canvasState.nodes, zoom]);

  const handleStartConnection = useCallback((nodeId: string, outputId: string) => {
    onUpdateCanvasState({
      isConnecting: true,
      connectionStart: { nodeId, outputId },
    });
    setConnectionPreview(null);
  }, [onUpdateCanvasState]);

  const handleEndConnection = useCallback((nodeId: string, inputId: string) => {
    if (canvasState.isConnecting && canvasState.connectionStart) {
      setPendingConnection({
        sourceNodeId: canvasState.connectionStart.nodeId,
        sourceOutputId: canvasState.connectionStart.outputId,
        targetNodeId: nodeId,
        targetInputId: inputId,
      });
      setPendingConnectionForm({ label: 'Next', conditionType: 'custom' });
    }
    onUpdateCanvasState({
      isConnecting: false,
      connectionStart: undefined,
    });
    setConnectionPreview(null);
    setHoverInput(null);
  }, [canvasState.isConnecting, canvasState.connectionStart, onUpdateCanvasState]);

  const confirmPendingConnection = useCallback(() => {
    if (!pendingConnection) return;
    const connection: WorkflowConnection = {
      id: `conn-${Date.now()}`,
      sourceNodeId: pendingConnection.sourceNodeId,
      sourceOutputId: pendingConnection.sourceOutputId,
      targetNodeId: pendingConnection.targetNodeId,
      targetInputId: pendingConnection.targetInputId,
      label: pendingConnectionForm.label.trim() || 'Next',
      conditionType: pendingConnectionForm.conditionType,
    };
    onAddConnection(connection);
    onUpdateCanvasState({ selectedConnectionId: connection.id, selectedNodeId: undefined });
    setPendingConnection(null);
  }, [onAddConnection, onUpdateCanvasState, pendingConnection, pendingConnectionForm]);

  const closePendingConnectionModal = useCallback(() => {
    setPendingConnection(null);
  }, []);

  const getEdgeColor = useCallback((connection: WorkflowConnection) => {
    if (connection.isLoopback) return '#f97316';
    if (connection.conditionType === 'fallback') return '#ef4444';
    if (connection.conditionType === 'outcome') return '#0f766e';
    if (connection.conditionType === 'intent') return '#7c3aed';
    // Default connections follow the theme; the others carry a meaning of their
    // own (loopback, fallback, outcome, intent) and keep their colours.
    return themeColor('#3b82f6');
  }, [themeColor]);

  const buildConnectorPath = useCallback((sourceX: number, sourceY: number, targetX: number, targetY: number) => {
    const controls = getBezierControls(sourceX, sourceY, targetX, targetY);
    return `M ${sourceX} ${sourceY} C ${controls.c1x} ${controls.c1y}, ${controls.c2x} ${controls.c2y}, ${targetX} ${targetY}`;
  }, [getBezierControls]);

  const renderConnection = (connection: WorkflowConnection) => {
    const sourceNode = canvasState.nodes.find(n => n.id === connection.sourceNodeId);
    const targetNode = canvasState.nodes.find(n => n.id === connection.targetNodeId);
    
    if (!sourceNode || !targetNode) {
      return null;
    }

    const sourceAnchor = getOutputAnchor(sourceNode, connection.sourceOutputId);
    const targetAnchor = getInputAnchor(targetNode, connection.targetInputId);
    const sourceX = sourceAnchor.x;
    const sourceY = sourceAnchor.y;
    const targetX = targetAnchor.x;
    const targetY = targetAnchor.y;

    const label = connection.label?.trim() || 'Next';
    const isSelected = canvasState.selectedConnectionId === connection.id;
    const strokeColor = getEdgeColor(connection);
    const pathD = buildConnectorPath(sourceX, sourceY, targetX, targetY);
    const curvePoint = getBezierPoint(sourceX, sourceY, targetX, targetY, 0.5);
    const normal = getBezierNormal(sourceX, sourceY, targetX, targetY, 0.5);
    const midX = curvePoint.x + normal.x * (connection.isLoopback ? 18 : 10);
    const midY = curvePoint.y + normal.y * (connection.isLoopback ? 18 : 10);
    const labelWidth = Math.max(72, label.length * 7.2);

    return (
      <React.Fragment key={connection.id}>
        <svg
          className="absolute inset-0 overflow-visible pointer-events-none"
          style={{ zIndex: 10 }}
        >
          <path
            d={pathD}
            fill="none"
            stroke={strokeColor}
            strokeWidth={isSelected ? 3 : 2.5}
            strokeDasharray={connection.isLoopback ? '3 9' : '2 8'}
            strokeLinecap="round"
            markerEnd={`url(#arrow-${connection.id})`}
            className="pointer-events-auto cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              onUpdateCanvasState({ selectedConnectionId: connection.id, selectedNodeId: undefined });
            }}
          />
          <defs>
            <marker
              id={`arrow-${connection.id}`}
              markerWidth="7"
              markerHeight="7"
              refX="5.5"
              refY="3.5"
              orient="auto"
              markerUnits="strokeWidth"
            >
              <path d="M 0 0 L 7 3.5 L 0 7 z" fill={strokeColor} />
            </marker>
          </defs>
        </svg>
        <Button tone="custom"
          className={`absolute -translate-x-1/2 -translate-y-1/2 rounded-full border px-3 py-1 text-[10px] font-medium shadow-sm ${
            isSelected
              ? 'border-blue-300 bg-blue-50 text-blue-700'
              : 'border-white/70 bg-white/95 text-gray-600'
          }`}
          style={{
            zIndex: 12,
            left: `${midX}px`,
            top: `${midY}px`,
            minWidth: `${labelWidth}px`,
          }}
          onClick={(e) => {
            e.stopPropagation();
            onUpdateCanvasState({ selectedConnectionId: connection.id, selectedNodeId: undefined });
          }}
          title="Edit transition"
        >
          <span className="inline-flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: strokeColor }} />
            {label}
          </span>
        </Button>
      </React.Fragment>
    );
  };

  const renderPreviewConnection = () => {
    if (!canvasState.isConnecting || !canvasState.connectionStart || !connectionPreview) {
      return null;
    }
    const sourceNode = canvasState.nodes.find(n => n.id === canvasState.connectionStart!.nodeId);
    if (!sourceNode) return null;

    const sourceAnchor = getOutputAnchor(sourceNode, canvasState.connectionStart.outputId);
    const sourceX = sourceAnchor.x;
    const sourceY = sourceAnchor.y;
    let targetX = connectionPreview.x;
    let targetY = connectionPreview.y;

    // Snap to hovered input port center
    if (hoverInput) {
      // Convert screen-space rect center to canvas-space using current zoom and canvas DOMRect
      const canvasRect = canvasRef.current?.getBoundingClientRect();
      if (canvasRect) {
        const centerX = hoverInput.rect.left + hoverInput.rect.width / 2;
        const centerY = hoverInput.rect.top + hoverInput.rect.height / 2;
        targetX = (centerX - canvasRect.left) / zoom;
        targetY = (centerY - canvasRect.top) / zoom;
      }
    }

    // Magnetic snap to closest input even if not precisely hovered
    if (!hoverInput) {
      const canvasRect = canvasRef.current?.getBoundingClientRect();
      const snapRadius = 28 / zoom; // px in canvas space
      if (canvasRect) {
        let bestDist = Infinity;
        let bestPoint: { x: number; y: number } | null = null;
        for (const node of canvasState.nodes) {
          // approximate input port near right edge middle of the node
          const firstInputId = node.inputs[0]?.id ?? 'in';
          const anchor = getInputAnchor(node, firstInputId);
          const ix = anchor.x;
          const iy = anchor.y;
          const dx = ix - targetX;
          const dy = iy - targetY;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < bestDist) {
            bestDist = d;
            bestPoint = { x: ix, y: iy };
          }
        }
        if (bestPoint && bestDist <= snapRadius) {
          targetX = bestPoint.x;
          targetY = bestPoint.y;
        }
      }
    }

    const pathD = buildConnectorPath(sourceX, sourceY, targetX, targetY);

    return (
      <svg className="absolute inset-0 overflow-visible pointer-events-none" style={{ zIndex: 15 }}>
        <path
          d={pathD}
          fill="none"
          stroke={themeColor('#60a5fa')}
          strokeWidth="1.75"
          strokeDasharray="2 8"
          strokeLinecap="round"
        />
      </svg>
    );
  };

  return (
    <div
      className="flex-1 relative overflow-hidden bg-gray-50"
      style={{ minHeight: '600px', height: '100%' }}
      onDrop={handleCanvasDrop}
      onDragOver={handleCanvasDragOver}
      onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); }}
      onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); }}
    >
      {/* Canvas Controls */}
      <div className="absolute top-4 right-4 z-30 flex space-x-2">
        <Button tone="custom"
          onClick={fitToFlow}
          className="px-3 py-2 bg-white rounded-lg shadow-md border border-gray-200 hover:bg-gray-50 transition-colors"
        >
          Fit Flow
        </Button>
        <Button tone="custom"
          onClick={centerToFlow}
          className="px-3 py-2 bg-white rounded-lg shadow-md border border-gray-200 hover:bg-gray-50 transition-colors"
        >
          Center Flow
        </Button>
        <Button tone="custom"
          onClick={() => setZoom(1)}
          className="px-3 py-2 bg-white rounded-lg shadow-md border border-gray-200 hover:bg-gray-50 transition-colors"
        >
          Reset Zoom
        </Button>
        <div className="px-3 py-2 bg-white rounded-lg shadow-md border border-gray-200">
          <span className="text-sm text-gray-600">
            {Math.round(zoom * 100)}%
          </span>
        </div>
      </div>

      {/* Canvas */}
      <div
        ref={canvasRef}
        className="w-full h-full relative cursor-grab active:cursor-grabbing"
        onClick={handleCanvasClick}
        onMouseDown={handleCanvasMouseDown}
        onMouseMove={handleCanvasMouseMove}
        onMouseUp={handleCanvasMouseUp}
        onMouseLeave={handleCanvasMouseUp}
        onWheel={handleWheel}
        style={{
          minHeight: '100%',
          transform: `translate(${canvasOffset.x}px, ${canvasOffset.y}px) scale(${zoom})`,
          transformOrigin: '0 0',
        }}
      >
        {/* Grid Background */}
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage: `
              linear-gradient(to right, ${cssColor('e5e7eb')} 1px, transparent 1px),
              linear-gradient(to bottom, ${cssColor('e5e7eb')} 1px, transparent 1px)
            `,
            backgroundSize: '20px 20px',
          }}
        />

        {/* Drop Zone Indicator */}
        {isDragOver && (
          <div className="absolute inset-0 bg-blue-50/50 border-2 border-dashed border-blue-400 rounded-lg flex items-center justify-center pointer-events-none" style={{ zIndex: 20 }}>
            <div className="text-center">
              <div className="text-4xl mb-2">📦</div>
              <p className="text-lg font-semibold text-blue-600">Drop node here</p>
              <p className="text-sm text-blue-500">Release to add to workflow</p>
            </div>
          </div>
        )}

        {/* Nodes */}

        {/* Nodes */}
        {canvasState.nodes.map((node) => (
          <WorkflowNode
            key={node.id}
            node={node}
            isSelected={canvasState.selectedNodeId === node.id}
            isConnecting={canvasState.isConnecting}
            onSelect={handleNodeSelect}
            onStartConnection={handleStartConnection}
            onEndConnection={handleEndConnection}
            onUpdateNode={onUpdateNode}
            onDeleteNode={onDeleteNode}
            onBeginDrag={handleBeginDragNode}
            onHoverInput={(nodeId, inputId, rect) => setHoverInput({ nodeId, inputId, rect })}
            onLeaveInput={() => setHoverInput(null)}
            onAddBranch={onAddRouterBranch}
            onOpenAgentConfig={onOpenAgentConfig}
            onPortLayout={(nodeId, portKind, portId, center) => {
              portAnchorsRef.current[`${nodeId}:${portKind}:${portId}`] = center;
            }}
          />
        ))}

        {/* Connections - Render after nodes so they appear on top */}
        {canvasState.connections.map(renderConnection)}
        {renderPreviewConnection()}
      </div>

      {/* Canvas Info */}
      <div className="absolute bottom-4 left-4 z-30">
        <div className="px-3 py-2 bg-white rounded-lg shadow-md border border-gray-200">
          <span className="text-sm text-gray-600">
            Nodes: {canvasState.nodes.length} | Connections: {canvasState.connections.length} | Flow: {Math.round(canvasBounds.width)} x {Math.round(canvasBounds.height)}
          </span>
        </div>
      </div>

      <Modal
        open={Boolean(pendingConnection)}
        onClose={closePendingConnectionModal}
        title="Create Transition"
        size="sm"
        footer={
          <>
            <Button tone="custom"
              type="button"
              className="rounded-md border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
              onClick={closePendingConnectionModal}
            >
              Cancel
            </Button>
            <Button tone="blue"
              type="button"
              className="rounded-md px-3 py-2 text-sm"
              onClick={confirmPendingConnection}
            >
              Add Transition
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-600">Label</span>
            <TextField tone="default"
              className="w-full rounded-md border px-3 py-2 text-sm"
              value={pendingConnectionForm.label}
              onChange={(e) => setPendingConnectionForm((prev) => ({ ...prev, label: e.target.value }))}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-600">Condition Type</span>
            <SelectField tone="default"
              className="w-full rounded-md border px-3 py-2 text-sm"
              value={pendingConnectionForm.conditionType}
              onChange={(e) =>
                setPendingConnectionForm((prev) => ({
                  ...prev,
                  conditionType: e.target.value as TransitionConditionType,
                }))
              }
            >
              {CONDITION_TYPE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </SelectField>
          </label>
        </div>
      </Modal>
    </div>
  );
};
